'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Heading } from '@//components/Heading';
import { LinkCloud } from '@//components/LinkCloud';
import type { MarketMetadata } from '@//market';
import { slugify } from '@//utils/slugify';
import Locations from '@//utils/vehicles-search/known-locations';
import styles from './SearchLinkCloud.module.scss';

/**
 * Truncate a number to N decimal places (no rounding).
 */
const truncate = (num: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.floor(num * factor) / factor;
};

/**
 * Find the matching location slug from known locations by comparing coordinates.
 */
const findLocationSlug = (lat: number, lng: number, radius: number): string | undefined => {
  const tLat = truncate(lat, 2);
  const tLon = truncate(lng, 2);

  for (const [slug, loc] of Object.entries(Locations)) {
    if (truncate(loc.lat, 2) === tLat && truncate(loc.lon, 2) === tLon && loc.radius === radius) {
      return slug;
    }
  }

  return undefined;
};

interface SearchLinkCloudLocationProps {
  searchPage: string;
  marketMetadata: MarketMetadata;
  currentFilters?: {
    manufacturer?: string;
    modelSeries?: string;
    allManufacturers?: string[];
  };
}

const SearchLinkCloudLocation = ({
  searchPage,
  marketMetadata,
  currentFilters,
}: SearchLinkCloudLocationProps) => {
  const tCommon = useTranslations('other.common');
  const tSearch = useTranslations('pages.search');

  const cloudLinksLocation = useMemo(() => {
    if (!marketMetadata.locationLinkCloud) {
      return [];
    }

    // Check if we have exactly one manufacturer selected
    const manufacturerCount = currentFilters?.allManufacturers?.length ?? 0;
    const hasMultipleFilters = manufacturerCount > 1;

    return marketMetadata.locationLinkCloud.map((location) => {
      // Try to find a matching known location slug for beautiful URLs
      const locationSlug = findLocationSlug(location.lat, location.lng, location.radius);

      // Build the beautiful URL path segments
      const pathSegments: string[] = [];

      // Only add manufacturer and model filters if we have exactly one manufacturer
      // Don't add filters if multiple manufacturers are selected
      if (!hasMultipleFilters && currentFilters?.manufacturer) {
        pathSegments.push(slugify(currentFilters.manufacturer));
      }
      if (!hasMultipleFilters && currentFilters?.modelSeries) {
        pathSegments.push(slugify(currentFilters.modelSeries));
      }

      // Add location slug if we found a match, otherwise fall back to query params
      let href: string;
      if (locationSlug) {
        pathSegments.push(locationSlug);
        href = `${searchPage}/${pathSegments.join('/')}`;
      } else {
        // Fallback to query params for unknown locations
        const params = new URLSearchParams();
        params.set('lat', location.lat.toString());
        params.set('lon', location.lng.toString());
        params.set('position.location', location.radius.toString());
        params.set(
          'position.name',
          `${location.name}, ${marketMetadata.region === 'sv' ? 'Sverige' : 'Norge'}`,
        );
        if (!hasMultipleFilters && currentFilters?.manufacturer) {
          params.set('manufacturer', currentFilters.manufacturer);
        }
        if (!hasMultipleFilters && currentFilters?.modelSeries) {
          params.set('modelSeries', currentFilters.modelSeries);
        }
        href = `${searchPage}?${params.toString()}`;
      }

      // Generate dynamic labels based on current filters
      let label: string;
      let title: string;
      let ariaLabel: string;

      // Use default labels if we have multiple manufacturers selected
      if (hasMultipleFilters) {
        // Default: multiple filters or no filters
        label = tSearch('linkCloud.vehiclesInLocation.label', {
          location: location.name,
        });
        title = tSearch('linkCloud.vehiclesInLocation.title', {
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.manufacturer && currentFilters?.modelSeries) {
        // If both manufacturer and model are filtered (single manufacturer)
        const model = `${currentFilters.manufacturer} ${currentFilters.modelSeries}`;
        label = tSearch('linkCloud.modelInLocation.label', {
          model,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model,
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.manufacturer) {
        // If only manufacturer is filtered (single manufacturer)
        label = tSearch('linkCloud.modelInLocation.label', {
          model: currentFilters.manufacturer,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model: currentFilters.manufacturer,
          location: location.name,
        });
        ariaLabel = title;
      } else if (currentFilters?.modelSeries) {
        // If only model is filtered (no manufacturer filter)
        label = tSearch('linkCloud.modelInLocation.label', {
          model: currentFilters.modelSeries,
          location: location.name,
        });
        title = tSearch('linkCloud.modelInLocation.title', {
          model: currentFilters.modelSeries,
          location: location.name,
        });
        ariaLabel = title;
      } else {
        // Default: no filters
        label = tSearch('linkCloud.vehiclesInLocation.label', {
          location: location.name,
        });
        title = tSearch('linkCloud.vehiclesInLocation.title', {
          location: location.name,
        });
        ariaLabel = title;
      }

      return {
        href,
        label,
        title,
        ariaLabel,
      };
    });
  }, [marketMetadata, searchPage, tSearch, currentFilters]);

  if (cloudLinksLocation.length === 0) {
    return null;
  }

  const headingId = 'search-link-cloud-heading-location';

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Heading level={2} size="xl" id={headingId}>
        {(() => {
          const manufacturerCount = currentFilters?.allManufacturers?.length ?? 0;
          const country =
            marketMetadata.region === 'sv' ? tCommon('region.sv') : tCommon('region.nb');

          // Show manufacturer-specific heading only if exactly one manufacturer is selected
          if (manufacturerCount === 1 && currentFilters?.manufacturer) {
            return tSearch('linkCloud.findNewAndUsedManufacturerInCountry', {
              manufacturer: currentFilters.manufacturer,
              country,
            });
          }

          // Default heading for no filters or multiple manufacturers
          return tSearch('linkCloud.findNewAndUsedCarsInCountry', { country });
        })()}
      </Heading>
      <LinkCloud
        links={cloudLinksLocation}
        testId="search-link-cloud-location"
        prefetch={false}
      />
    </section>
  );
};

export default SearchLinkCloudLocation;
